import React, { useEffect, useRef } from "react";
import { FinancialChartReference } from "models/AiResearch";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import styles from "./style.scss";
import {
  formatDateString,
  formatThousandthsDigits
} from "components/AiResearch/utils/chartsFormat";

interface Props {
  reference: FinancialChartReference;
}

export const FinancialChart: React.FC<Props> = ({ reference }) => {
  const chartRef = useRef<HighchartsReact>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const seriesList = reference.data.content.series;

  // ResizeObserverを使用して、コンテナのサイズ変更に応じてチャートを再描画
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const container = chartContainerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      chartRef.current?.chart.reflow();
    });

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, []);

  const options: Highcharts.Options = {
    title: {
      text: "",
      useHTML: true,
      style: {
        whiteSpace: "normal"
      }
    },
    yAxis: {
      title: { text: "" },
      labels: {
        formatter() {
          return formatThousandthsDigits(this.value, seriesList);
        }
      }
    },
    xAxis: {
      title: { text: reference.data.content.xLabel },
      categories:
        seriesList[0]?.values.map(value => formatDateString(value.x)) ?? [],
      gridLineWidth: 1,
      gridLineColor: "#e6e6e6",
      labels: {
        rotation: -45
      }
    },
    tooltip: {
      headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
      pointFormatter() {
        const formattedValue = formatThousandthsDigits(this.y, seriesList);
        return `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${formattedValue}</b><br/>`;
      }
    },
    series: seriesList.map(series => ({
      type: reference.data.content.chartType,
      data: series.values.map(value => value.y),
      name: series.name
    })),
    exporting: {
      enabled: false
    },
    credits: {
      enabled: false
    }
  };

  return (
    <div className={styles.container}>
      <span className={styles.citationNumber}>{reference.citationNumber}</span>
      <h2 className={styles.title}>{reference.data.meta.name}</h2>
      <p className={styles.description}>{reference.data.content.description}</p>
      <div className={styles.chartContainer} ref={chartContainerRef}>
        <HighchartsReact
          ref={chartRef}
          highcharts={Highcharts}
          options={options}
          containerProps={{ className: styles.chartInner }}
        />
      </div>
    </div>
  );
};
